<template>
  <div class="p-file-root">
    <div
      v-if="variant === 'compact'"
      class="compact-wrapper bg-primary-20 q-py-md q-px-sm rounded-borders"
    >
      <div class="flex items-center justify-between">
        <div>
          <span class="text-sm text-neutral">{{ label }}</span
          ><span v-if="required || validations?.required" class="text-error-60">
            *</span
          >
        </div>
        <div>
          <PBtn
            v-if="!hasFiles || $attrs.multiple !== undefined"
            dense
            flat
            color="primary-100"
            :disable="$attrs.disable"
            @click="triggerFilePicker"
          >
            <slot name="action-icon">
              <UploadDocumentIcon class="icon--md" />
            </slot>
            <PTooltip>Upload File</PTooltip>
          </PBtn>
          <PBtn
            v-else
            dense
            flat
            color="primary-100"
            :loading="removeLoading"
            :disable="$attrs.disable || removeLoading"
            @click="removeFile(0)"
          >
            <RemoveIcon class="icon--md" />
            <PTooltip>Remove</PTooltip>
          </PBtn>
        </div>
      </div>
      <div v-if="help">
        <span :class="helpClasses">{{ help }}</span>
      </div>

      <div v-if="hasFiles">
        <div
          v-for="(file, i) in filesList"
          :key="i"
          :class="[
            $attrs.multiple !== undefined
              ? `flex items-center justify-between${i > 0 ? ' q-mt-sm' : ''}`
              : '',
          ]"
        >
          <q-linear-progress v-if="isLoading(file)" indeterminate />
          <div
            v-else
            :class="[
              'text-sm text-primary-100 text-weight-medium cursor-pointer',
            ]"
            @click="onViewFile(file)"
          >
            {{ file.name }}
          </div>
          <PBtn
            v-if="!isLoading(file) && $attrs.multiple !== undefined"
            dense
            flat
            color="primary-100"
            :disable="$attrs.disable"
            @click="removeFile(i)"
          >
            <RemoveIcon class="icon--md" />
            <PTooltip>Remove</PTooltip>
          </PBtn>
        </div>
      </div>
      <span v-if="internalError || $attrs.error" class="text-negative text-xs">
        {{ internalError || $attrs["error-message"] }}
      </span>
    </div>

    <template v-else>
      <p v-if="label" :class="computedLabelClasses">
        <span>{{ label }}</span>
        <span v-if="required || validations?.required" class="text-negative">
          *
        </span>
      </p>
      <p v-if="help" :class="helpClasses">
        <span>{{ help }}</span>
      </p>
    </template>

    <div :class="variant !== 'compact' ? 'flex items-center' : 'hidden'">
      <q-file
        ref="component"
        v-bind="$attrs"
        :model-value="qFileModelValue"
        :class="[variant, { 'cursor-pointer col': variant !== 'compact' }]"
        :rules="validationRules"
        :dense="variant === 'compact'"
        :borderless="variant === 'compact'"
        :outlined="variant !== 'compact'"
        :no-error-icon="variant !== 'compact'"
        :hide-bottom-space="variant === 'compact'"
        :error="
          variant === 'compact' ? !!internalError || $attrs.error : $attrs.error
        "
        :error-message="
          variant === 'compact'
            ? internalError || $attrs['error-message']
            : $attrs['error-message']
        "
        @update:model-value="onModelValueUpdate"
        @rejected="onRejected"
      >
        <template
          v-for="(slot, index) of Object.keys($slots)"
          :key="index"
          #[slot]="slotProps"
        >
          <slot v-if="slotProps" v-bind="slotProps" :name="slot"></slot>
          <slot v-else :name="slot"></slot>
        </template>

        <template v-if="variant === 'upload'" #file> </template>

        <template v-if="variant !== 'compact'" #default>
          <div
            v-if="variant === 'upload'"
            class="absolute-full column items-center justify-center"
          >
            <UploadDocumentIcon class="icon q-mb-sm" />
            <span class="text-weight-medium">
              Drag and drop or click to browse files
            </span>
          </div>
          <div
            v-if="variant === 're-upload' && !$attrs.modelValue"
            class="absolute-full flex items-center"
          >
            <UploadDocumentIcon class="icon q-mr-sm" />
            <span class="text-weight-medium">
              Drag and drop or click to browse files
            </span>
          </div>
          <div
            v-if="variant === 're-upload'"
            class="row items-center z-index-1"
          >
            <div class="col">
              <PBtn dense flat round @click.prevent.stop="removeFile">
                <RemoveIcon class="icon--md" />
              </PBtn>
            </div>
          </div>
        </template>
      </q-file>
    </div>
  </div>
</template>
<script setup>
import { isEmpty } from "assets/scripts/utilities/helpers";
import validationsToRules from "assets/scripts/utilities/validations";
import RemoveIcon from "assets/icons/remove.svg";
import UploadDocumentIcon from "assets/icons/upload_document.svg";

const props = defineProps({
  label: { type: String, default: "" },
  labelClasses: { type: [String, Array, Object], default: "" },
  help: { type: String, default: "" },
  helpClasses: {
    type: [String, Array, Object],
    default: "text-caption text-neutral-60",
  },
  required: { type: Boolean, default: false },
  validations: { type: Object, default: () => ({}) },
  rules: { type: Array, default: () => [] },
  variant: {
    type: String,
    default: "upload",
    validator: (v) => ["upload", "re-upload", "compact"].includes(v),
  },
  loadingState: {
    type: Object,
    default: () => ({}),
  },
  removeLoading: {
    type: Boolean,
    default: false,
  },
  deferRemoval: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "view-file",
  "update:model-value",
  "rejected",
  "remove-file",
]);

const component = ref();
const internalError = ref("");

const attrs = useAttrs();

const hasFiles = computed(() => {
  if (Array.isArray(attrs.modelValue)) {
    return attrs.modelValue.length > 0;
  }

  return !!attrs.modelValue;
});

const filesList = computed(() => {
  if (!attrs.modelValue) return [];

  return Array.isArray(attrs.modelValue)
    ? attrs.modelValue
    : [attrs.modelValue];
});

const isNativeFile = (value) => {
  return typeof File !== "undefined" && value instanceof File;
};

const isNativeFileList = (value) => {
  return typeof FileList !== "undefined" && value instanceof FileList;
};

const qFileModelValue = computed(() => {
  if (Array.isArray(attrs.modelValue)) {
    return attrs.modelValue.filter(isNativeFile);
  }

  if (isNativeFile(attrs.modelValue) || isNativeFileList(attrs.modelValue)) {
    return attrs.modelValue;
  }

  return null;
});

const triggerFilePicker = () => {
  component.value?.$el.click();
};

const isLoading = (file) => {
  if (file && file._id) return !!props.loadingState[file._id];

  if (file && file.name) return !!props.loadingState[file.name];

  return false;
};

const getFilesCount = (value) => {
  if (Array.isArray(value)) {
    return value.length;
  }

  return value ? 1 : 0;
};

const applyCompactFilesLimit = (val) => {
  if (!(props.variant === "compact" && attrs["max-files"])) {
    return false;
  }

  const max = parseInt(attrs["max-files"], 10);
  const currentCount = getFilesCount(attrs.modelValue);
  const newCount = getFilesCount(val);

  if (newCount <= max) {
    return false;
  }

  if (currentCount < max && Array.isArray(val)) {
    const sliced = val.slice(0, max);

    emit("update:model-value", sliced);

    internalError.value = `Maximum ${max} files allowed`;
  }

  return true;
};

const onModelValueUpdate = (val) => {
  if (applyCompactFilesLimit(val)) {
    return;
  }

  internalError.value = "";

  emit("update:model-value", val);
};

const onRejected = (entries) => {
  internalError.value = entries
    .map((e) =>
      e.failedPropValidation === "max-files"
        ? `Maximum ${attrs["max-files"]} files allowed`
        : e.failedPropValidation,
    )
    .join(", ");

  emit("rejected", entries);
};

const onViewFile = (file) => {
  if (file?._id) {
    emit("view-file", file);
  } else {
    const url = URL.createObjectURL(file);

    window.open(url, "_blank");
  }
};

const removeFile = (index) => {
  if (index !== undefined) {
    if (Array.isArray(attrs.modelValue)) {
      const removedFile = attrs.modelValue[index];

      if (!props.deferRemoval) {
        const updated = [...attrs.modelValue];

        updated.splice(index, 1);

        emit("update:model-value", updated);
      }

      emit("remove-file", index, removedFile);
    } else {
      const removedFile = attrs.modelValue;

      if (!props.deferRemoval) {
        emit("update:model-value", null);
      }

      emit("remove-file", index, removedFile);
    }

    internalError.value = "";
  } else {
    if (Array.isArray(attrs.modelValue)) {
      unref(component)?.removeFile(attrs.modelValue[0]);
    } else {
      unref(component)?.removeFile(attrs.modelValue);
    }

    emit("remove-file", index);
  }
};

const computedLabelClasses = computed(() => {
  const defaults = "text-body2 text-weight-medium q-mb-sm";

  if (Array.isArray(props.labelClasses)) {
    return [defaults, ...props.labelClasses];
  } else if (typeof props.labelClasses === "object") {
    return { [defaults]: true, ...props.labelClasses };
  } else {
    return `${defaults} ${props.labelClasses}`;
  }
});

const validationRules = computed(() => {
  if (props.required || !isEmpty(props.validations)) {
    const isRequired = props.required || props.validations?.required;

    const baseRules = validationsToRules({
      key: "name",
      label: props.validations?.label || props.label,
      // In compact mode the q-file model-value only contains native File
      // objects, so uploaded file objects (plain objects) would make the
      // built-in required rule return false. Use hasFiles instead.
      required: props.variant === "compact" ? false : isRequired,
      type: props.validations?.type,
      ...props.validations,
    });

    if (props.variant === "compact" && isRequired) {
      return [() => hasFiles.value || false, ...baseRules, ...props.rules];
    }

    return [...baseRules, ...props.rules];
  }

  return [];
});

defineExpose({
  component,
});
</script>

<style lang="scss" scoped>
.q-field {
  :deep(.q-field__inner) {
    .q-field {
      &__control {
        background: $neutral-10;
        height: fit-content;
        min-height: 54px;
        overflow: hidden;

        &::after {
          height: 100%;
          min-height: 100%;
        }

        &::before {
          border-color: $neutral-50;
        }

        &:not(.text-negative):hover::after {
          border-color: $primary-80;
        }

        &:not(.text-negative):focus-within::after {
          border-color: $primary;
        }

        &-container {
          height: inherit;
          min-height: inherit;
        }

        .q-file {
          &__dnd {
            outline-offset: -2px;
          }
        }
      }

      &__marginal {
        height: 54px;
      }

      &__native {
        height: inherit;
        min-height: inherit;
        z-index: 1;

        &::placeholder {
          color: $neutral-50;
        }
      }
    }
  }

  &.q-textarea :deep(.q-field__inner) {
    .q-field {
      &__control {
        height: auto;
        padding-right: 0;

        &::after {
          height: auto;
        }
      }

      &__native {
        padding-right: 12px;
        resize: none;
      }
    }
  }

  &.prepend-bg :deep(.q-field__inner) {
    .q-field {
      &__control {
        padding: 0 12px 0 0;
      }

      &__prepend {
        background: $primary-40;

        justify-content: center;

        min-width: 54px;

        padding: 0 12px;
      }

      &__native {
        padding-left: 12px;
      }
    }
  }
}

.q-field.compact {
  :deep(.q-field__control) {
    display: none !important;
  }
}

.p-file-root:has(.q-field--error) {
  .compact-wrapper {
    outline: 2px solid $error-60;
  }
}

.q-field.upload {
  :deep(.q-field__inner) {
    .q-field {
      &__control {
        min-height: 78px;
      }

      &__marginal {
        height: 78px;
      }
    }
  }
}
</style>
